import * as stylex from '@stylexjs/stylex'
import { type PropsWithChildren } from 'react'
import { Fade, type FadeProps } from '../Fade/Fade'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const dynamicStyles = stylex.create({
  backgroundColor: (opacity: number) => ({ backgroundColor: `rgba(0, 0, 0, ${opacity})` }),
  zIndex: (zIndex: number) => ({ zIndex }),
})

const _baseStyles = stylex.create({
  root: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    opacity: 0,
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
})

export interface BackdropProps extends Omit<FadeProps, 'xStyles'> {
  /**
   * 是否顯示背景遮罩
   */
  open?: boolean
  /**
   * 自訂內部子元素樣式覆寫，分為 base 與 fade 兩組
   */
  xStyles?: {
    base?: Partial<XStyles<keyof typeof _baseStyles>>
    fade?: FadeProps['xStyles']
  }
  /**
   * 遮罩不透明度
   * @default 0.56
   */
  opacity?: number
  /**
   * 遮罩的 z-index 層級
   * @default -1
   */
  zIndex?: number
}

/**
 * Backdrop 背景遮罩元件，用於覆蓋頁面內容以聚焦前景元素。
 * 通常搭配 Modal、Dialog 等元件使用。
 */
export const Backdrop = (props: PropsWithChildren<BackdropProps>) => {
  const { open, opacity = 0.56, rootStyle, xStyles, zIndex = -1, ...rest } = props
  const baseStyles = useXStyles(_baseStyles, xStyles?.base)

  return (
    <Fade
      in={open}
      xStyles={xStyles?.fade}
      rootStyle={[
        baseStyles.root,
        dynamicStyles.backgroundColor(opacity),
        dynamicStyles.zIndex(zIndex),
        rootStyle,
      ]}
      {...rest}
    />
  )
}
